<template>
	<MainContainer
		header-selected="blog"
		:loading="status == 'pending'"
		:fetched="status == 'success'"
		:error="error"
		no-company-branding
		@try-again="refresh"
	>
		<div class="flex flex-col px-4 lg:px-16">
			<BlogHeroSection />
		</div>
		<BlogTopArticlesSection
			v-if="!qSearchText && page == 1"
			:articles="data?.most_popular_post_data?.posts || []"
			class="bg-gradient-to-t from-slate-100 to-white !pb-16 !pt-12"
			@go-to-all="goToArticles"
		/>
		<BlogCategoriesSection v-if="!qSearchText && page == 1" :categories="categories" />
		<div ref="contentContainerEl" class="flex flex-col px-4 pb-20 pt-12">
			<div class="flex w-full max-w-screen-xl flex-col self-center">
				<div class="mb-6 flex items-center">
					<span class="flex-grow text-2xl font-bold text-slate-950">
						{{ $t("latest_articles") + (page > 1 ? ` (${$t("page_n", { page })})` : "") }}
					</span>
					<span
						v-if="false"
						class="text-white cursor-pointer rounded-md border-2 border-slate-950 bg-slate-950 px-[15px] py-[9px] font-bold duration-300 hover:bg-white hover:text-slate-950"
					>
						{{ $t("show_all") }}
					</span>
					<span class="text-slate-400 text-xs">
						{{ $t("n_articles", { n: data?.post_data?.total }, data?.post_data?.total || 0) }}
					</span>
				</div>
				<div class="grid w-full max-w-screen-xl grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
					<NuxtLink
						v-for="article in data?.post_data?.posts"
						:key="article.id"
						:to="$routeUrl.BlogArticleUrl(article.id)"
						class="flex flex-col"
					>
						<ArticleCard :article="article" class="flex-grow" />
					</NuxtLink>
					<div
						v-if="!data?.post_data?.posts?.length"
						class="bg-white border-slate-300 col-span-3 flex flex-col rounded-lg border py-6"
					>
						<BaseEmptyBox class="w-full max-w-lg self-center" />
					</div>
				</div>
				<div
					v-if="data?.post_data?.total"
					class="border-slate-400 mt-16 flex flex-col rounded-lg border bg-gradient-to-t from-slate-50 to-white px-3 py-3"
				>
					<BasePagination
						:current="page"
						:total="data?.post_data?.total || 0"
						:limit="state.limit"
						:link="(p) => $routeUrl.BlogPageUrl(p)"
						class="self-center"
					/>
				</div>
			</div>
		</div>
		<BlogTopArticlesSection
			v-if="qSearchText || page != 1"
			:articles="data?.most_popular_post_data?.posts || []"
			class="bg-gradient-to-b from-slate-100 to-white !pb-16 !pt-12"
			@go-to-all="goToArticles"
		/>
	</MainContainer>
</template>

<script lang="ts" setup>
const categoryStore = useCategoryStore()
const cmsStore = useCmsStore()
const app = useNuxtApp()
const route = useRoute()

const state = reactive({
	limit: 12
})

const contentContainerEl = shallowRef<HTMLElement>()

const page = computed(() => Math.max(1, Number(route.params.page) || 1))
const qSearchText = computed(() => String(route.query.s || ""))

const categories = computed(() => categoryStore.categories?.filter((c) => c.type == "article") || [])

const { data, status, error, refresh } = useLazyAsyncData(
	String(route.path),
	() =>
		cmsStore.contentBundle({
			company_id: null,
			queries: [
				{
					query_name: "post_data" as const,
					query_type: "posts",
					types: "article",
					page: page.value,
					limit: state.limit,
					search: qSearchText.value
				},
				{
					query_name: "most_popular_post_data" as const,
					query_type: "posts",
					types: "article",
					page: 1,
					limit: 4,
					search: qSearchText.value,
					has_image: true
				}
			]
		}),
	{
		watch: [() => route.query]
	}
)

const goToArticles = (smooth: boolean = true) => {
	if (!contentContainerEl.value) return
	window.scrollTo({
		top: contentContainerEl.value.offsetTop - 64,
		behavior: smooth ? "smooth" : "instant"
	})
}

useHead(() => ({
	title: app.$i18n.t("app_blog") + " | " + app.$i18n.t("app_motto")
}))
</script>
